<template>
  <div>
    <div class="mb-2 d-flex align-items-center gap-2">
      <router-link
        :to="{ name: 'cost-prices-additional-fees' }"
        class="d-flex align-items-center gap-2"
      >
        <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
        {{ $t('Back to List') }}
      </router-link>
    </div>
    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="costPricesAdditionalFeeDetailForm">
        <b-form ref="costPricesAdditionalFeeDetailFormElement" class="" @submit.prevent="submit">
          <b-row>
            <b-col sm="12" md="12">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="provider">{{ t('Provider') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Provider')"
                  :rules="type === 'edit' ? 'required' : ''"
                >
                  <v-select
                    id="provider"
                    :disabled="type === 'edit'"
                    v-model="values.providerId"
                    :reduce="item => item.value"
                    :options="providers"
                    :placeholder="t('Make a Choice')"
                    label="label"
                    :tabindex="2"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Description') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Description')"
                  rules="required"
                >
                  <b-form-input
                    id="description"
                    v-model="values.description"
                    name="description"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Description')"
                    :tabindex="1"
                    autocomplete="off"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="price">{{ t('Price') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Price')"
                  rules="required|doubleOrInteger|positive"
                >
                  <b-form-input
                    id="price"
                    v-model="values.price"
                    name="price"
                    type="number"
                    step="0.0001"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Price')"
                    :tabindex="2"
                    autocomplete="off"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="pricingType">{{ t('Pricing Type') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Pricing Type')"
                  rules="required"
                >
                  <b-form-select
                    v-model="values.pricingType"
                    :options="pricingTypeOptions"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Pricing Type')"
                    :tabindex="3"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="status">{{ t('Status') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Status')" rules="required">
                  <b-form-select
                    v-model="values.status"
                    :options="statusOptions"
                    :state="errors.length > 0 ? false : null"
                    :placeholder="t('Status')"
                    :tabindex="4"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12" class="d-flex justify-content-end">
              <b-button
                variant="primary"
                class="mt-1 d-flex align-items-center"
                type="submit"
                :tabindex="5"
              >
                {{ submitting ? t('Please Wait') : type === 'new' ? t('Add') : t('Save') }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>
<script>
import {
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BFormSelect,
  BButton,
  BOverlay
} from 'bootstrap-vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { togglePasswordVisibility } from '@core/mixins/ui/forms';
import { helper } from '@core/mixins/helper';
import vSelect from 'vue-select';

export default {
  components: {
    BOverlay,
    BButton,
    BFormInput,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    BFormSelect,
    vSelect,
    ValidationObserver,
    ValidationProvider
  },

  mixins: [togglePasswordVisibility, helper],
  props: {
    type: {
      type: String,
      default: 'new'
    },
    submitting: {
      type: Boolean,
      default: false
    },
    values: {
      type: Object,
      default() {
        return {
          providerId: null,
          description: '',
          price: '',
          pricingType: '',
          status: ''
        };
      }
    }
  },
  data() {
    return {
      statusTypeOptions: [],
      pricingType: [],
      providers: []
    };
  },

  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon';
    },

    pricingTypeOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.pricingType && this.pricingType.length > 0) {
        options.push(
          ...this.pricingType.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    },
    statusOptions() {
      const options = [{ text: this.$t('Make a Choice'), value: '' }];
      if (this.statusTypeOptions && this.statusTypeOptions.length > 0) {
        options.push(
          ...this.statusTypeOptions.map(item => ({
            text: item.info,
            value: item.value
          }))
        );
      }
      return options;
    }
  },

  async created() {
    const [pricingType, statusTypeOptions, providers] = await Promise.all([
      this.getProviderAdditionalFeePricingType(),
      this.getProviderAdditionalFeeStatus(),
      this.getProviders({ paginated: false })
    ]);

    this.pricingType = pricingType;
    this.statusTypeOptions = statusTypeOptions;
    this.providers = providers.map(item => ({
      value: item.id,
      label: item.name
    }));
  },

  methods: {
    submit() {
      this.$refs.costPricesAdditionalFeeDetailForm.validate().then(success => {
        if (success) {
          this.$emit('submit', {
            ...this.values,
            providerId: this.values.providerId
          });
        }
      });
    },

    resetForm() {
      this.$refs.providerFeeDetailFormElement.reset();
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
